import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, FormField, Input, Modal, Select, Spinner, Textarea } from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import {
  Agent,
  CreateAgentRequest,
  FesConfig,
  ModelDefinition,
  ReasoningEffort,
  Skill,
  createAgent,
  deleteAgent,
  getAgent,
  getModels,
  getSkills,
  updateAgent,
} from '../../fes';
import { Team } from '../../fes';
import { Session } from '../../session';
import { EditorSections } from '../EditorSections';
import { AgentsTable } from './AgentsTable';

type SystemInstruction = {
  name?: string;
  content?: string;
  [key: string]: unknown;
};

type AgentEditorMode = 'create' | 'edit';

const defaultAgentDraft: Record<string, unknown> = {
  name: '',
  description: '',
  attachedSkillIds: [],
  llmConfig: {
    provider: 'azure',
    model: '',
    reasoningEffort: 'low',
  },
  systemInstructions: [],
};

function getStringValue(data: Record<string, unknown> | null | undefined, field: string): string {
  const value = data?.[field];

  return typeof value === 'string' ? value : '';
}

function normalizeProvider(provider: string): string {
  return provider === 'azure-openai' ? 'azure' : provider;
}

function getNestedStringValue(data: Record<string, unknown> | null | undefined, parent: string, field: string): string {
  const parentValue = data?.[parent];

  if (!parentValue || typeof parentValue !== 'object' || Array.isArray(parentValue)) {
    return '';
  }

  return getStringValue(parentValue as Record<string, unknown>, field);
}

function getStringArrayValue(data: Record<string, unknown> | null | undefined, field: string): string[] {
  const value = data?.[field];

  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function getSystemInstructions(data: Record<string, unknown> | null | undefined): SystemInstruction[] {
  const value = data?.systemInstructions;

  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is SystemInstruction => Boolean(item) && typeof item === 'object' && !Array.isArray(item));
}

function mergeAgentData(baseAgent: Agent | null, data: Record<string, unknown>, agentId: string): Agent {
  const merged = {
    ...(baseAgent || {}),
    ...data,
    agentId,
  };
  const name = typeof merged.name === 'string' && merged.name.trim() ? merged.name : baseAgent?.name || agentId;

  return {
    ...merged,
    agentId,
    name,
  };
}

function compactString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function buildCreateAgentRequest(data: Record<string, unknown>): CreateAgentRequest {
  const llmConfig = data.llmConfig && typeof data.llmConfig === 'object' && !Array.isArray(data.llmConfig)
    ? (data.llmConfig as Record<string, unknown>)
    : undefined;
  const systemInstructions = getSystemInstructions(data);
  const attachedSkillIds = getStringArrayValue(data, 'attachedSkillIds');
  const codexAssets = Array.isArray(data.codexAssets)
    ? data.codexAssets.filter((asset): asset is Record<string, unknown> => Boolean(asset) && typeof asset === 'object' && !Array.isArray(asset))
    : undefined;

  return {
    name: getStringValue(data, 'name').trim(),
    ...(compactString(data.description) ? { description: compactString(data.description) } : {}),
    ...(data.isGlobal === true ? { isGlobal: true } : {}),
    ...(llmConfig ? { llmConfig } : {}),
    ...(systemInstructions.length > 0 ? { systemInstructions } : {}),
    ...(codexAssets && codexAssets.length > 0 ? { codexAssets } : {}),
    ...(attachedSkillIds.length > 0 ? { attachedSkillIds } : {}),
  };
}

function buildUpdateAgentRequest(data: Record<string, unknown>): Record<string, unknown> {
  const request = { ...data };

  delete request.templateId;

  // Older records still carry maxTokens and temperature, which Codex ignores; saving drops them.
  const llmConfig = request.llmConfig;
  if (llmConfig && typeof llmConfig === 'object' && !Array.isArray(llmConfig)) {
    const nextLlmConfig = { ...(llmConfig as Record<string, unknown>) };
    delete nextLlmConfig.maxTokens;
    delete nextLlmConfig.temperature;
    request.llmConfig = nextLlmConfig;
  }

  return request;
}

export function AgentsView({
  agents,
  creatingAgent,
  editingAgentId,
  fesConfig,
  onAgentDeleted,
  onAgentSaved,
  onCreateAgentClose,
  onCreateAgentOpen,
  onEditAgentClose,
  onEditAgentOpen,
  session,
  teams,
  tokens,
}: {
  agents: Agent[];
  creatingAgent?: boolean;
  editingAgentId?: string;
  fesConfig: FesConfig;
  session: Session;
  teams: Team[];
  onAgentDeleted: (agentId: string) => void;
  onAgentSaved: (agent: Agent) => void;
  onCreateAgentClose?: () => void;
  onCreateAgentOpen?: () => void;
  onEditAgentClose?: () => void;
  onEditAgentOpen?: (agent: Agent) => void;
  tokens: AuthTokens;
}) {
  const latestAgentRequest = useRef(0);
  const [createTeamId, setCreateTeamId] = useState(session.activeTeamId ?? '');
  const [agentEditorMode, setAgentEditorMode] = useState<AgentEditorMode>('edit');
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [agentDraft, setAgentDraft] = useState<Record<string, unknown> | null>(null);
  const [isLoadingAgent, setIsLoadingAgent] = useState(false);
  const [hasLoadedAgent, setHasLoadedAgent] = useState(false);
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [editError, setEditError] = useState<string>();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [areSkillsLoading, setAreSkillsLoading] = useState(false);
  const [skillsError, setSkillsError] = useState<string>();
  const [skillFilter, setSkillFilter] = useState('');
  const [models, setModels] = useState<ModelDefinition[]>([]);
  const [areModelsLoading, setAreModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string>();
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  const [isDeletingAgent, setIsDeletingAgent] = useState(false);
  const [deleteError, setDeleteError] = useState<string>();
  const [listSearch, setListSearch] = useState('');

  const loadSkillsForEditor = useCallback(() => {
    if (skills.length > 0 || areSkillsLoading) {
      return;
    }

    setAreSkillsLoading(true);
    setSkillsError(undefined);

    const load = async () => {
      try {
        const skillsResponse = await getSkills(fesConfig, tokens);
        setSkills(skillsResponse.data || []);
      } catch (error) {
        setSkillsError(error instanceof Error ? error.message : 'Unable to load skills.');
      } finally {
        setAreSkillsLoading(false);
      }
    };

    void load();
  }, [areSkillsLoading, fesConfig, skills.length, tokens]);

  const loadModelsForEditor = useCallback(() => {
    if (models.length > 0 || areModelsLoading) return;
    setAreModelsLoading(true);
    setModelsError(undefined);
    void getModels(fesConfig, tokens)
      .then(response => setModels(response.data || []))
      .catch(error => setModelsError(error instanceof Error ? error.message : 'Unable to load models.'))
      .finally(() => setAreModelsLoading(false));
  }, [areModelsLoading, fesConfig, models.length, tokens]);

  const handleEditOpen = useCallback((agent: Agent) => {
    if (!agent.agentId) {
      return;
    }

    const requestId = latestAgentRequest.current + 1;
    latestAgentRequest.current = requestId;
    setAgentEditorMode('edit');
    setEditingAgent(agent);
    setAgentDraft(agent);
    setIsLoadingAgent(true);
    setHasLoadedAgent(false);
    setIsSavingAgent(false);
    setEditError(undefined);
    setSkillFilter('');
    loadSkillsForEditor();
    loadModelsForEditor();

    const load = async () => {
      try {
        const agentResponse = await getAgent(fesConfig, tokens, agent.agentId);

        if (latestAgentRequest.current !== requestId) {
          return;
        }

        const fullAgent = agentResponse.data || agent;
        setEditingAgent(fullAgent);
        setAgentDraft(fullAgent);
        setHasLoadedAgent(true);
      } catch (error) {
        if (latestAgentRequest.current !== requestId) {
          return;
        }

        setEditError(error instanceof Error ? error.message : 'Unable to load full agent.');
      } finally {
        if (latestAgentRequest.current === requestId) {
          setIsLoadingAgent(false);
        }
      }
    };

    void load();
  }, [fesConfig, loadModelsForEditor, loadSkillsForEditor, tokens]);

  const handleCreateOpen = useCallback(() => {
    latestAgentRequest.current += 1;
    setAgentEditorMode('create');
    setEditingAgent({ agentId: '', name: '' });
    setAgentDraft({ ...defaultAgentDraft, llmConfig: { ...(defaultAgentDraft.llmConfig as Record<string, unknown>) } });
    setIsLoadingAgent(false);
    setHasLoadedAgent(true);
    setIsSavingAgent(false);
    setEditError(undefined);
    setSkillFilter('');
    loadSkillsForEditor();
    loadModelsForEditor();
  }, [loadModelsForEditor, loadSkillsForEditor]);

  const handleEditClose = useCallback((notifyRoute = true) => {
    if (isSavingAgent) {
      return;
    }

    latestAgentRequest.current += 1;
    setEditingAgent(null);
    setAgentDraft(null);
    setAgentEditorMode('edit');
    setIsLoadingAgent(false);
    setHasLoadedAgent(false);
    setEditError(undefined);
    if (notifyRoute) {
      if (agentEditorMode === 'create') {
        onCreateAgentClose?.();
      } else {
        onEditAgentClose?.();
      }
    }
  }, [agentEditorMode, isSavingAgent, onCreateAgentClose, onEditAgentClose]);

  useEffect(() => {
    if (creatingAgent) {
      if (agentEditorMode !== 'create' || !editingAgent) {
        handleCreateOpen();
      }

      return;
    }

    if (agentEditorMode === 'create') {
      handleEditClose(false);
      return;
    }

    if (!editingAgentId) {
      if (editingAgent) {
        handleEditClose(false);
      }

      return;
    }

    if (editingAgent?.agentId === editingAgentId) {
      return;
    }

    const agentFromList = agents.find(agent => agent.agentId === editingAgentId);

    handleEditOpen(agentFromList || { agentId: editingAgentId, name: editingAgentId });
  }, [agentEditorMode, agents, creatingAgent, editingAgent, editingAgentId, handleCreateOpen, handleEditClose, handleEditOpen]);

  const updateAgentDraft = (updater: (data: Record<string, unknown>) => Record<string, unknown>) => {
    setAgentDraft(currentDraft => (currentDraft ? updater(currentDraft) : currentDraft));
    setEditError(undefined);
  };

  const updateAgentDraftField = (field: string, value: string) => {
    updateAgentDraft(data => ({
      ...data,
      [field]: value,
    }));
  };

  const updateAgentDraftNestedField = (parent: string, field: string, value: string) => {
    updateAgentDraft(data => {
      const parentValue = data[parent];
      const parentData =
        parentValue && typeof parentValue === 'object' && !Array.isArray(parentValue)
          ? (parentValue as Record<string, unknown>)
          : {};

      return {
        ...data,
        [parent]: {
          ...parentData,
          [field]: value,
        },
      };
    });
  };

  const selectModel = (modelId: string) => {
    const selected = models.find(modelDefinition => modelDefinition.modelId === modelId);
    if (!selected) return;
    updateAgentDraft(data => {
      const llmConfig = data.llmConfig && typeof data.llmConfig === 'object' && !Array.isArray(data.llmConfig)
        ? data.llmConfig as Record<string, unknown>
        : {};
      return {
        ...data,
        llmConfig: {
          ...llmConfig,
          provider: selected.provider,
          model: selected.model,
          reasoningEffort: selected.supportedReasoningEfforts.includes(reasoningEffort as ReasoningEffort)
            ? reasoningEffort
            : selected.defaultReasoningEffort,
        },
      };
    });
  };

  const updateAgentDraftArrayField = (field: string, values: string[]) => {
    updateAgentDraft(data => ({
      ...data,
      [field]: values,
    }));
  };

  const updateSystemInstruction = (index: number, field: keyof SystemInstruction, value: string) => {
    updateAgentDraft(data => {
      const systemInstructions = getSystemInstructions(data);

      return {
        ...data,
        systemInstructions: systemInstructions.map((instruction, instructionIndex) =>
          instructionIndex === index
            ? {
                ...instruction,
                [field]: value,
              }
            : instruction
        ),
      };
    });
  };

  const handleAddSystemInstruction = () => {
    updateAgentDraft(data => ({
      ...data,
      systemInstructions: [...getSystemInstructions(data), { name: '', content: '' }],
    }));
  };

  const handleRemoveSystemInstruction = (index: number) => {
    updateAgentDraft(data => ({
      ...data,
      systemInstructions: getSystemInstructions(data).filter((_instruction, instructionIndex) => instructionIndex !== index),
    }));
  };

  const handleSkillToggle = (skillId: string, checked: boolean) => {
    const attachedSkillIds = getStringArrayValue(agentDraft, 'attachedSkillIds');
    const nextSkillIds = checked
      ? Array.from(new Set([...attachedSkillIds, skillId]))
      : attachedSkillIds.filter(attachedSkillId => attachedSkillId !== skillId);

    updateAgentDraftArrayField('attachedSkillIds', nextSkillIds);
  };

  const handleSelectVisibleSkills = () => {
    const attachedSkillIds = getStringArrayValue(agentDraft, 'attachedSkillIds');
    const visibleSkillIds = filteredSkills.map(skill => skill.skillId);

    updateAgentDraftArrayField('attachedSkillIds', Array.from(new Set([...attachedSkillIds, ...visibleSkillIds])));
  };

  const handleClearSkills = () => {
    updateAgentDraftArrayField('attachedSkillIds', []);
  };

  const handleAgentSave = () => {
    if (!agentDraft) {
      return;
    }

    const agentData = agentEditorMode === 'create' ? agentDraft : buildUpdateAgentRequest(agentDraft);
    setIsSavingAgent(true);
    setEditError(undefined);

    const save = async () => {
      try {
        if (agentEditorMode === 'create') {
          const createRequest = buildCreateAgentRequest(agentData);
          const agentResponse = await createAgent(fesConfig, tokens, createRequest, {
            teamId: createRequest.isGlobal ? undefined : createTeamId || undefined,
          });
          const savedAgent = agentResponse.data || mergeAgentData(editingAgent, createRequest, createRequest.name);

          onAgentSaved(savedAgent);
          handleEditClose();
          return;
        }

        const agentId = editingAgent?.agentId;

        if (!agentId) {
          return;
        }

        const agentResponse = await updateAgent(fesConfig, tokens, agentId, agentData);
        const savedAgent = agentResponse.data || mergeAgentData(editingAgent, agentData, agentId);

        onAgentSaved(savedAgent);
        handleEditClose();
      } catch (error) {
        setEditError(error instanceof Error ? error.message : 'Unable to save agent.');
      } finally {
        setIsSavingAgent(false);
      }
    };

    void save();
  };

  const handleDeleteClose = () => {
    if (isDeletingAgent) {
      return;
    }

    setAgentToDelete(null);
    setDeleteError(undefined);
  };

  const handleAgentDelete = () => {
    const agentId = agentToDelete?.agentId;

    if (!agentId) {
      return;
    }

    setIsDeletingAgent(true);
    setDeleteError(undefined);

    const remove = async () => {
      try {
        await deleteAgent(fesConfig, tokens, agentId);
        onAgentDeleted(agentId);
        setAgentToDelete(null);
      } catch (error) {
        setDeleteError(error instanceof Error ? error.message : 'Unable to delete agent.');
      } finally {
        setIsDeletingAgent(false);
      }
    };

    void remove();
  };

  const isCreatingAgent = agentEditorMode === 'create';
  const isGlobalAgent = agentDraft?.isGlobal === true;
  const canManageAgent = (agent: Agent) => !agent.isGlobal || session.isAdmin;
  const canManageEditingAgent = isCreatingAgent || !editingAgent?.isGlobal || session.isAdmin;
  const isAgentDraftEditable = Boolean(
    agentDraft && hasLoadedAgent && !isLoadingAgent && !isSavingAgent && canManageEditingAgent
  );
  const agentName = getStringValue(agentDraft, 'name');
  const canSaveAgent = Boolean(
    hasLoadedAgent &&
      !isLoadingAgent &&
      !isSavingAgent &&
      canManageEditingAgent &&
      agentDraft &&
      agentName.trim() &&
      (isCreatingAgent || editingAgent?.agentId)
  );
  const agentDescription = getStringValue(agentDraft, 'description');
  const agentId = getStringValue(agentDraft, 'agentId') || editingAgent?.agentId || '-';
  const userId = getStringValue(agentDraft, 'userId') || '-';
  const attachedSkillIds = getStringArrayValue(agentDraft, 'attachedSkillIds');
  const attachedSkillIdSet = new Set(attachedSkillIds);
  const systemInstructions = getSystemInstructions(agentDraft);
  const normalizedSkillFilter = skillFilter.trim().toLowerCase();
  const sortedSkills = [...skills].sort((firstSkill, secondSkill) =>
    (firstSkill.name || firstSkill.skillId).localeCompare(secondSkill.name || secondSkill.skillId)
  );
  const filteredSkills = normalizedSkillFilter
    ? sortedSkills.filter(skill =>
        [skill.name, skill.description, skill.skillId].some(value => value?.toLowerCase().includes(normalizedSkillFilter))
      )
    : sortedSkills;
  const knownSkillIds = new Set(skills.map(skill => skill.skillId));
  const unknownSkillIds = attachedSkillIds.filter(skillId => !knownSkillIds.has(skillId));
  const model = getNestedStringValue(agentDraft, 'llmConfig', 'model');
  const reasoningEffort = getNestedStringValue(agentDraft, 'llmConfig', 'reasoningEffort');
  const provider = getNestedStringValue(agentDraft, 'llmConfig', 'provider');
  const activeModels = models.filter(modelDefinition => modelDefinition.status === 'active');
  const selectedModel = models.find(modelDefinition =>
    normalizeProvider(modelDefinition.provider) === normalizeProvider(provider)
    && modelDefinition.model === model);
  const supportedEfforts = selectedModel?.supportedReasoningEfforts ?? [];
  const legacyModel = Boolean(model && (!selectedModel || selectedModel.status !== 'active'));
  const normalizedListSearch = listSearch.trim().toLowerCase();
  const filteredAgents = normalizedListSearch
    ? agents.filter(agent =>
        [agent.name, agent.description, agent.agentId].some(value => value?.toLowerCase().includes(normalizedListSearch))
      )
    : agents;

  return (
    <>
      <div className="app-table-toolbar">
        <Input
          aria-label="Search agents"
          className="app-table-search"
          onChange={event => setListSearch(event.target.value)}
          placeholder="Search agents"
          type="search"
          value={listSearch}
        />
        <Button icon="semantic-plus" onClick={() => (onCreateAgentOpen ? onCreateAgentOpen() : handleCreateOpen())}>
          New Agent
        </Button>
      </div>
      <AgentsTable
        agents={filteredAgents}
        canManageAgent={canManageAgent}
        onDeleteAgent={agent => {
          setAgentToDelete(agent);
          setDeleteError(undefined);
        }}
        onEditAgent={agent => (onEditAgentOpen ? onEditAgentOpen(agent) : handleEditOpen(agent))}
      />
      {editingAgent ? (
        <Modal
          buttonCancelLabel={isCreatingAgent ? 'Cancel create agent' : 'Cancel edit agent'}
          buttonCancelText="Cancel"
          buttonCloseLabel="Close"
          buttonConfirmLabel={isCreatingAgent ? 'Create agent' : `Save agent ${editingAgent.name || editingAgent.agentId}`}
          buttonConfirmText={isSavingAgent ? (isCreatingAgent ? 'Creating...' : 'Saving...') : isCreatingAgent ? 'Create' : 'Save'}
          confirmationDisabled={!canSaveAgent}
          onCancel={() => handleEditClose()}
          onClose={() => handleEditClose()}
          onConfirm={handleAgentSave}
          shown
          large
          left
          stretch
          className="app-agent-editor-modal"
          status={editError ? 'error' : undefined}
          title={isCreatingAgent ? 'Create agent' : 'Edit agent'}
        >
          <>
            <div className="app-agent-editor">
              {!isCreatingAgent ? (
                <div className="app-agent-editor-summary" aria-label="Agent identity">
                  <div className="app-agent-editor-summary-item">
                    <span>Agent ID</span>
                    <strong>{agentId}</strong>
                  </div>
                  <div className="app-agent-editor-summary-item">
                    <span>User ID</span>
                    <strong>{userId}</strong>
                  </div>
                </div>
              ) : null}

              {isLoadingAgent ? (
                <div className="app-detail-loading app-detail-loading--compact" role="status">
                  <Spinner center>Loading agent...</Spinner>
                </div>
              ) : null}
              {editError ? (
                <div className="app-result app-team-error" role="alert">
                  {editError}
                </div>
              ) : null}

              <div className="app-agent-editor-main">
                <EditorSections
                  sections={[
                    {
                      id: 'general',
                      content: (
                <section className="app-agent-editor-section" aria-labelledby="agent-common-fields-heading">
                  <h3 id="agent-common-fields-heading" className="app-agent-editor-section-title">
                    General
                  </h3>
                  <div className="app-agent-editor-grid">
                    <FormField labelForId="agent-name" labelText="Name" notion type="input">
                      <Input
                        id="agent-name"
                        value={agentName}
                        onChange={event => updateAgentDraftField('name', event.target.value)}
                        readOnly={!isAgentDraftEditable}
                      />
                    </FormField>
                    {isCreatingAgent ? (
                      <>
                        {session.isAdmin ? (
                          <Checkbox
                            id="agent-global"
                            checked={isGlobalAgent}
                            disabled={!isAgentDraftEditable}
                            onChange={event => {
                              const checked = event.target.checked;
                              updateAgentDraft(data => ({ ...data, isGlobal: checked }));
                              if (checked) setCreateTeamId('');
                            }}
                          >
                            Global agent (available to every user)
                          </Checkbox>
                        ) : null}
                        {!isGlobalAgent ? (
                          <FormField
                            labelForId="agent-team-select"
                            labelText="Team"
                            type="select"
                            hint={
                              createTeamId
                                ? 'Everyone on this team can see and edit the agent.'
                                : 'Without a team the agent is visible only to you.'
                            }
                          >
                            <Select
                              id="agent-team-select"
                              value={createTeamId}
                              onChange={event => setCreateTeamId(event.target.value)}
                              disabled={!isAgentDraftEditable || teams.length === 0}
                            >
                              <Select.Item value="">No team (private to me)</Select.Item>
                              {teams.map(team => (
                                <Select.Item key={team.teamId} value={team.teamId}>
                                  {team.name || team.teamId}
                                </Select.Item>
                              ))}
                            </Select>
                          </FormField>
                        ) : null}
                      </>
                    ) : null}
                    <FormField labelForId="agent-provider" labelText="Provider" type="input">
                      <Input
                        id="agent-provider"
                        value={provider}
                        readOnly
                      />
                    </FormField>
                    <FormField labelForId="agent-model" labelText="Model" type="select">
                      <Select
                        id="agent-model"
                        value={selectedModel?.status === 'active' ? selectedModel.modelId : (model ? '__legacy__' : '')}
                        onChange={event => selectModel(event.target.value)}
                        disabled={!isAgentDraftEditable || areModelsLoading}
                      >
                        <Select.Item value="" disabled>Select a model</Select.Item>
                        {legacyModel ? <Select.Item value="__legacy__">{`${model} (${provider || 'unknown'} · legacy)`}</Select.Item> : null}
                        {activeModels.map(modelDefinition => (
                          <Select.Item key={modelDefinition.modelId} value={modelDefinition.modelId}>
                            {`${modelDefinition.displayName} (${modelDefinition.provider})`}
                          </Select.Item>
                        ))}
                      </Select>
                    </FormField>
                    <FormField labelForId="agent-reasoning-effort" labelText="Reasoning Effort" type="select">
                      <Select
                        id="agent-reasoning-effort"
                        value={reasoningEffort}
                        onChange={event => updateAgentDraftNestedField('llmConfig', 'reasoningEffort', event.target.value)}
                        disabled={!isAgentDraftEditable || supportedEfforts.length === 0}
                      >
                        {reasoningEffort && !supportedEfforts.includes(reasoningEffort as ReasoningEffort)
                          ? <Select.Item value={reasoningEffort}>{`${reasoningEffort} (legacy)`}</Select.Item>
                          : null}
                        {supportedEfforts.map(effort => <Select.Item key={effort} value={effort}>{effort}</Select.Item>)}
                      </Select>
                    </FormField>
                    {areModelsLoading ? <Spinner center>Loading models...</Spinner> : null}
                    {modelsError ? <div className="app-result app-team-error" role="alert">{modelsError}</div> : null}
                    {legacyModel ? (
                      <div className="app-result" role="status">
                        This agent references a model that is not in the central catalog. It is preserved until you select a replacement.
                      </div>
                    ) : null}
                    <FormField labelForId="agent-description" labelText="Description" type="textarea">
                      <Textarea
                        id="agent-description"
                        value={agentDescription}
                        onChange={event => updateAgentDraftField('description', event.target.value)}
                        readOnly={!isAgentDraftEditable}
                        rows={4}
                      />
                    </FormField>
                  </div>
                </section>
                      ),
                    },

                    {
                      id: 'skills',
                      content: (
                <section className="app-agent-editor-section" aria-labelledby="agent-skills-heading">
                  <div className="app-agent-editor-section-header">
                    <h3 id="agent-skills-heading" className="app-agent-editor-section-title">
                      Skills
                    </h3>
                    <span className="app-agent-editor-count">{attachedSkillIds.length} selected</span>
                  </div>
                  <div className="app-agent-skills-toolbar">
                    <FormField labelForId="agent-skill-filter" labelText="Filter Skills" type="input">
                      <Input
                        id="agent-skill-filter"
                        value={skillFilter}
                        onChange={event => setSkillFilter(event.target.value)}
                        readOnly={!isAgentDraftEditable}
                      />
                    </FormField>
                    <div className="app-agent-skills-actions">
                      <Button secondary onClick={handleSelectVisibleSkills} disabled={!isAgentDraftEditable || filteredSkills.length === 0}>
                        Select visible
                      </Button>
                      <Button secondary onClick={handleClearSkills} disabled={!isAgentDraftEditable || attachedSkillIds.length === 0}>
                        Clear
                      </Button>
                    </div>
                  </div>
                  {areSkillsLoading ? (
                    <div className="app-detail-loading app-detail-loading--compact" role="status">
                      <Spinner center>Loading skills...</Spinner>
                    </div>
                  ) : null}
                  {skillsError ? (
                    <div className="app-result app-team-error" role="alert">
                      {skillsError}
                    </div>
                  ) : null}
                  <div className="app-agent-skills-list" role="group" aria-labelledby="agent-skills-heading">
                    {filteredSkills.map(skill => (
                      <Checkbox
                        key={skill.skillId}
                        id={`agent-skill-${skill.skillId}`}
                        checked={attachedSkillIdSet.has(skill.skillId)}
                        disabled={!isAgentDraftEditable}
                        onChange={event => handleSkillToggle(skill.skillId, event.target.checked)}
                      >
                        <span className="app-agent-skill-option">
                          <span className="app-agent-skill-name">{skill.name || skill.skillId}</span>
                          <span className="app-agent-skill-id">{skill.skillId}</span>
                          {skill.description ? <span className="app-agent-skill-description">{skill.description}</span> : null}
                        </span>
                      </Checkbox>
                    ))}
                    {!areSkillsLoading && filteredSkills.length === 0 ? <p className="app-empty">No skills found.</p> : null}
                    {unknownSkillIds.map(skillId => (
                      <Checkbox
                        key={skillId}
                        id={`agent-skill-unknown-${skillId}`}
                        checked
                        disabled={!isAgentDraftEditable}
                        onChange={event => handleSkillToggle(skillId, event.target.checked)}
                      >
                        <span className="app-agent-skill-option">
                          <span className="app-agent-skill-name">Unknown skill</span>
                          <span className="app-agent-skill-id">{skillId}</span>
                        </span>
                      </Checkbox>
                    ))}
                  </div>
                </section>
                      ),
                    },

                    {
                      id: 'instructions',
                      content: (
                <section className="app-agent-editor-section app-agent-editor-section--wide" aria-labelledby="agent-instructions-heading">
                  <div className="app-agent-editor-section-header">
                    <h3 id="agent-instructions-heading" className="app-agent-editor-section-title">
                      System Instructions
                    </h3>
                    <Button secondary onClick={handleAddSystemInstruction} disabled={!isAgentDraftEditable}>
                      Add instruction
                    </Button>
                  </div>
                  <div className="app-agent-instructions-list">
                    {systemInstructions.map((instruction, index) => (
                      <div className="app-agent-instruction" key={index}>
                        <div className="app-agent-instruction-header">
                          <FormField labelForId={`agent-system-instruction-name-${index}`} labelText="Name" type="input">
                            <Input
                              id={`agent-system-instruction-name-${index}`}
                              value={typeof instruction.name === 'string' ? instruction.name : ''}
                              onChange={event => updateSystemInstruction(index, 'name', event.target.value)}
                              readOnly={!isAgentDraftEditable}
                            />
                          </FormField>
                          <Button secondary onClick={() => handleRemoveSystemInstruction(index)} disabled={!isAgentDraftEditable}>
                            Remove
                          </Button>
                        </div>
                        <FormField labelForId={`agent-system-instruction-content-${index}`} labelText="Content" type="textarea">
                          <Textarea
                            id={`agent-system-instruction-content-${index}`}
                            value={typeof instruction.content === 'string' ? instruction.content : ''}
                            onChange={event => updateSystemInstruction(index, 'content', event.target.value)}
                            readOnly={!isAgentDraftEditable}
                            rows={6}
                          />
                        </FormField>
                      </div>
                    ))}
                    {systemInstructions.length === 0 ? <p className="app-empty">No system instructions found.</p> : null}
                  </div>
                </section>
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          </>
        </Modal>
      ) : null}
      {agentToDelete ? (
        <Modal
          buttonCancelLabel="Cancel delete agent"
          buttonCancelText="Cancel"
          buttonCloseLabel="Close"
          buttonConfirmLabel={`Delete agent ${agentToDelete.name || agentToDelete.agentId}`}
          buttonConfirmText={isDeletingAgent ? 'Deleting...' : 'Delete'}
          confirmationDisabled={isDeletingAgent}
          onCancel={handleDeleteClose}
          onClose={handleDeleteClose}
          onConfirm={handleAgentDelete}
          shown
          status="warning"
          title="Delete agent"
        >
          <>
            {deleteError ? (
              <div className="app-result app-team-error" role="alert">
                {deleteError}
              </div>
            ) : null}
            <p className="app-modal-copy">
              Delete agent <strong>{agentToDelete.name || agentToDelete.agentId}</strong>? This cannot be undone.
            </p>
          </>
        </Modal>
      ) : null}
    </>
  );
}
